import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RuleInterface } from '@vendasta/automata';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { SendNotificationFormComponent } from '../../send-notification-common/send-notification-form/send-notification-form.component';
import {
  GenericNotificationID,
  HighPriorityAutomationNotificationID,
  ShoppingCartAbandonedNotificationID,
} from '../../send-notification-to-the-user/constants';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { NotificationSettingsLinkComponent } from '../../../common/shared-form-elements';

@Component({
  selector: 'automata-send-notification-user-panel',
  templateUrl: './send-notification-user-panel.component.html',
  styleUrls: ['./send-notification-user-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    SendNotificationFormComponent,
    NotificationSettingsLinkComponent,
  ],
})
export class SendNotificationUserPanelComponent extends CommonActionPanelComponent implements OnInit {
  genericNotificationID = GenericNotificationID;
  highPriorityNotificationID = HighPriorityAutomationNotificationID;
  shoppingCartAbandonedNotificationID = ShoppingCartAbandonedNotificationID;
  defaultLinkTemplate: string;
  notificationIdControl: UntypedFormControl = new UntypedFormControl('', Validators.required);
  form: UntypedFormGroup;

  @ViewChild('notificationForm', { static: false })
  sendNotificationForm: SendNotificationFormComponent;

  constructor(protected readonly fb: UntypedFormBuilder) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.defaultLinkTemplate = `{{business_app_url}}/account/location/{{business_id}}/dashboard`;
    const jsonData = this.step ? JSON.parse(this.step.data) : {};
    const initialNotificationTypeId = jsonData?.notification_id || this.genericNotificationID;
    this.notificationIdControl.setValue(initialNotificationTypeId);
    this.form = this.fb.group({
      notificationId: this.notificationIdControl,
    });

    // There's nothing asynchronous here, so we can simply enable saving.
    this.savingEnabled = true;
  }

  getRules(): RuleInterface[] {
    return [];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    jsonData.notification_id = this.notificationIdControl.value;
    jsonData.email_notification = {};
    jsonData.in_app_notification = {};

    if (this.notificationIdControl.value !== ShoppingCartAbandonedNotificationID) {
      const notification = this.sendNotificationForm.getData();
      jsonData.notification_channel = notification?.notificationChannel;
      jsonData.email_notification = {
        link_template: notification?.emailNotification?.linkTemplate,
        button_text_template: notification?.emailNotification?.buttonTextTemplate,
        subject_template: notification?.emailNotification?.subjectTemplate,
        text_template: notification?.emailNotification?.textTemplate,
      };
      jsonData.in_app_notification = {
        link_template: notification?.inAppNotification?.linkTemplate,
        text_template: notification?.inAppNotification?.textTemplate,
      };
    }

    return jsonData;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    if (this.notificationIdControl.value !== ShoppingCartAbandonedNotificationID) {
      return [this.form, this.sendNotificationForm.form];
    }
    return [this.form];
  }

  isFormValid(): boolean {
    if (this.notificationIdControl.value !== ShoppingCartAbandonedNotificationID) {
      return !this.form.invalid && this.sendNotificationForm.isFormValid();
    }
    return !this.form.invalid;
  }
}
